import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Lock, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/site";
import { scrollToId } from "../hooks/useSmoothScroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    if (l.route) {
      navigate(l.route);
      return;
    }
    const id = l.id || l;
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  const goAdmin = () => {
    setOpen(false);
    navigate("/login");
  };

  const goAudit = () => {
    setOpen(false);
    navigate("/brand-audit");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0A0C0F] border-b border-[#2A2D34]" : "border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <Logo data-testid="nav-logo-btn" />

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => l.id === "method" ? (
            <div key={l.id} className="relative group">
              <button
                onClick={() => go(l)}
                data-testid={`nav-link-${l.id}`}
                className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-[#8A939B] hover:text-white transition-colors relative"
              >
                {l.label}
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-[#C6FF00] transition-all duration-300 group-hover:w-full" />
              </button>
              <div className="pointer-events-none absolute left-0 top-full pt-4 opacity-0 translate-y-1 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0">
                <div className="w-60 border border-[#2A2D34] bg-[#0E1013] py-2 shadow-2xl">
                  <button onClick={() => go(l)} data-testid="nav-dropdown-method"
                    className="block w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#8A939B] transition-colors hover:bg-white/5 hover:text-[#C6FF00]">
                    The H.U.G.H. Method
                  </button>
                  <button onClick={() => { setOpen(false); navigate("/playbook"); }} data-testid="nav-dropdown-ebook"
                    className="block w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#8A939B] transition-colors hover:bg-white/5 hover:text-[#C6FF00]">
                    Ebook & Workbook
                  </button>
                  <button onClick={() => { setOpen(false); navigate("/blog"); }} data-testid="nav-dropdown-blog"
                    className="block w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#8A939B] transition-colors hover:bg-white/5 hover:text-[#C6FF00]">
                    Playbook Blog
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              key={l.id}
              onClick={() => go(l)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-semibold uppercase tracking-wider text-[#8A939B] hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-[#C6FF00] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={goAdmin}
            data-testid="nav-client-login-btn"
            className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white hover:text-[#C6FF00] transition-colors"
          >
            <Lock className="h-4 w-4" /> HL Portal
          </button>
          <button
            onClick={goAudit}
            data-testid="nav-brand-audit-btn"
            className="flex items-center gap-2 rounded-full border border-[#C6FF00] text-[#C6FF00] font-bold uppercase text-xs tracking-wider px-5 py-3.5 hover:bg-[#C6FF00] hover:text-[#0A0C0F] transition-colors duration-300"
          >
            Free Brand Audit
          </button>
          <button
            onClick={() => navigate("/book")}
            data-testid="nav-cta-btn"
            className="btn-shine flex items-center gap-2 rounded-full bg-[#C6FF00] text-[#0A0C0F] font-bold uppercase text-xs tracking-wider px-6 py-3.5 hover:bg-white transition-colors duration-300"
          >
            Book Strategy Call
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A0C0F] border-b border-[#2A2D34]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l)}
                  className="text-left text-2xl font-bold uppercase tracking-tight text-white"
                  data-testid={`mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/playbook"); }}
                className="text-left text-2xl font-bold uppercase tracking-tight text-white"
                data-testid="mobile-link-ebook"
              >
                Ebooks
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/blog"); }}
                className="text-left text-2xl font-bold uppercase tracking-tight text-white"
                data-testid="mobile-link-blog"
              >
                Playbook Blog
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/book"); }}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-white text-[#0A0C0F] font-bold uppercase text-sm px-6 py-4 active:bg-[#C6FF00]"
                data-testid="mobile-cta-btn"
              >
                Book Strategy Call <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
              <button
                onClick={goAudit}
                className="flex items-center justify-center gap-2 rounded-full border border-[#C6FF00] text-[#C6FF00] font-bold uppercase text-sm px-6 py-4"
                data-testid="mobile-brand-audit-btn"
              >
                Free Brand Audit
              </button>
              <button
                onClick={goAdmin}
                className="flex items-center justify-center gap-2 border border-[#2A2D34] text-white font-bold uppercase text-sm px-6 py-4 hover:border-[#C6FF00] hover:text-[#C6FF00] transition-colors"
                data-testid="mobile-client-login-btn"
              >
                <Lock className="h-4 w-4" /> HL Portal
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
